import IConfig from 'app/config/Config'
import { onSnapshot } from 'mobx-state-tree'

import * as storage from '../../utils/storage'
import { convertNullToUndefined } from '../../utils/convert-null-to-undefined'
import { Environment } from '../environment'
import { RootStoreModel, RootStore } from './root-store'
import { UserStoreSnapshot } from '../user-store'

/**
 * The key we'll be saving our state as within async storage.
 */
const ROOT_STATE_STORAGE_KEY = 'root'

/**
 * Setup the root state.
 */
export async function setupRootStore(config: IConfig, initialUserStore?: UserStoreSnapshot | null) {
  let rootStore: RootStore
  let data: any
  const environment = new Environment(config)

  // Convert null values to undefined for MST compatibility
  const sanitizedUserStore = initialUserStore ? convertNullToUndefined(initialUserStore) : undefined

  try {
    // load data from storage and convert null values to undefined for MST compatibility
    const storedData = (await storage.load(ROOT_STATE_STORAGE_KEY)) || {}
    data = convertNullToUndefined(storedData)
    
    // Reset isAuthInProgress if it was stuck from a previous session
    if (data?.userStore?.isAuthInProgress) {
      data.userStore.isAuthInProgress = false
    }
    
    // SSR data is the source of truth from the server session.
    // If we have SSR user data, use it. If SSR explicitly says no user
    // (initialUserStore === null), clear stored auth to prevent stale
    // tokens in localStorage from recreating a broken session.
    if (sanitizedUserStore) {
      data = {
        ...data,
        userStore: sanitizedUserStore,
      }
    } else if (initialUserStore === null && data?.userStore) {
      delete data.userStore.user
      delete data.userStore.auth
    }

    rootStore = RootStoreModel.create(data, environment)
  } catch (e) {
    // if there's any problems loading, then let's at least fallback to an empty state
    // instead of crashing.
    const fallbackData = sanitizedUserStore ? { userStore: sanitizedUserStore } : {}
    rootStore = RootStoreModel.create(fallbackData, environment)

    let msg = ''

    if (typeof e === 'string') {
      msg = e
    } else if (e instanceof Error) {
      msg = e.message
    } else {
      msg = JSON.stringify(e)
    }

    // but please inform us what happened
    __DEV__ && console.error(msg, null)
  }

  // track changes & save to storage
  onSnapshot(rootStore, (snapshot) =>
    storage.save(ROOT_STATE_STORAGE_KEY, snapshot)
  )

  return rootStore
}
